import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Banknote,
  IndianRupee,
  Package,
  RefreshCw,
  ShoppingBag,
  TrendingUp,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import {
  getDashboardCategories,
  getDashboardChannels,
  getDashboardFulfillmentTimeseries,
  getDashboardGst,
  getDashboardPayments,
  getDashboardReturns,
  getDashboardReturnsTimeseries,
  getDashboardSummary,
  getDashboardTimeseries,
  getDashboardTopProducts,
} from '../../api/dashboard';
import { formatCount, formatInr, formatPct } from '../../utils/formatInr';
import { mapAnalyticsError } from '../../api/analyticsParams';
import {
  CHANNELS,
  MAX_DASHBOARD_DAYS,
  MAX_REPORT_DAYS,
  PERIOD_PRESETS,
  daysInclusive,
  defaultGranularity,
  formatAsOf,
  formatRangeLabel,
  formatShortRange,
  hourGranularityAllowed,
  previousWindow,
  resolveRange,
} from './period';
import WidgetShell from './WidgetShell';
import { ReportsHistory, ReportsToolbar, useReports } from './ReportsRail';
import {
  CategoriesBars,
  ChannelDonut,
  DualLineChart,
  PaymentsBars,
  TrendChart,
  WaterfallChart,
} from './charts';
import styles from './Analytics.module.css';
import { colors } from '../../theme';

const RETURNS_EXCHANGE_SERIES = [
  { key: 'returns', label: 'Returns', color: '#D32F2F' },
  { key: 'exchanges', label: 'Exchange', color: '#B8850A', dashed: true },
];

const SCHEDULING_SERIES = [
  { key: 'scheduled', label: 'Scheduled', color: colors.primaryDark },
  { key: 'completed', label: 'Completed', color: '#2E7D32' },
  { key: 'failed', label: 'Delivery failed', color: '#D32F2F', dashed: true },
];

function useAsyncResource(loader, deps, { enabled = true, onSettled } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const seq = useRef(0);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const reload = useCallback(async () => {
    if (!enabled) return;
    const gen = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const result = await loader();
      if (gen !== seq.current) return;
      setData(result);
    } catch (err) {
      if (gen !== seq.current) return;
      setData(null);
      setError(mapAnalyticsError(err));
    } finally {
      if (gen === seq.current) {
        setLoading(false);
        onSettledRef.current?.();
      }
    }
  }, [...deps, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) return undefined;
    reload();
    return undefined;
  }, [reload, enabled]);

  return { data, loading: enabled ? loading : true, error, reload };
}

export default function Analytics() {
  const { user } = useAuth();
  const isAdmin = user?.groups?.includes('Admin');
  const [detailReady, setDetailReady] = useState(false);
  const reports = useReports(isAdmin, { enabled: detailReady });
  const markDetailsReady = useCallback(() => setDetailReady(true), []);

  const [preset, setPreset] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [channel, setChannel] = useState('all');
  const [compare, setCompare] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [trendMetric, setTrendMetric] = useState('revenue');
  const [productMetric, setProductMetric] = useState('revenue');
  const [categoryMetric, setCategoryMetric] = useState('revenue');
  const [granularity, setGranularity] = useState(() =>
    defaultGranularity(resolveRange('this_month').from, resolveRange('this_month').to)
  );

  const range = useMemo(
    () => resolveRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );
  const dayCount = daysInclusive(range.from, range.to);
  // Inclusive ≤ 90 allowed; disable live widgets only when > 90 (not ≥).
  const rangeOver90 = dayCount > MAX_DASHBOARD_DAYS;
  const reportRangeTooLong = dayCount > MAX_REPORT_DAYS;
  const compareWindow = compare ? previousWindow(range.from, range.to) : null;
  const hourOk = hourGranularityAllowed(range.from, range.to);

  useEffect(() => {
    setGranularity(defaultGranularity(range.from, range.to));
  }, [range.from, range.to]);

  useEffect(() => {
    if (!hourOk && granularity === 'hour') {
      setGranularity(defaultGranularity(range.from, range.to));
    }
  }, [hourOk, granularity, range.from, range.to]);

  useEffect(() => {
    if (!isAdmin && trendMetric === 'gross_profit') setTrendMetric('revenue');
    if (!isAdmin && productMetric === 'profit') setProductMetric('revenue');
    if (!isAdmin && categoryMetric === 'profit') setCategoryMetric('revenue');
  }, [isAdmin, trendMetric, productMetric, categoryMetric]);

  const queryBase = useMemo(
    () => ({
      from: range.from,
      to: range.to,
      channel,
      period: range.period,
      _r: refreshKey,
    }),
    [range, channel, refreshKey]
  );

  useEffect(() => {
    setDetailReady(rangeOver90);
  }, [range.from, range.to, channel, refreshKey, rangeOver90]);

  const summary = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardSummary(
            { from: range.from, to: range.to, channel, compare, period: range.period },
            user.idToken
          ),
    [queryBase, rangeOver90, compare, user.idToken],
    { onSettled: markDetailsReady }
  );

  const timeseries = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardTimeseries(
            {
              from: range.from,
              to: range.to,
              channel,
              compare,
              granularity,
              metric: trendMetric,
              period: range.period,
            },
            user.idToken
          ),
    [queryBase, rangeOver90, granularity, trendMetric, compare, user.idToken]
  );

  const channels = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardChannels(
            { from: range.from, to: range.to, channel, period: range.period },
            user.idToken
          ),
    [queryBase, rangeOver90, user.idToken]
  );

  const payments = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardPayments(
            { from: range.from, to: range.to, channel, period: range.period },
            user.idToken
          ),
    [queryBase, rangeOver90, user.idToken]
  );

  const topProducts = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardTopProducts(
            {
              from: range.from,
              to: range.to,
              channel,
              metric: productMetric,
              period: range.period,
              limit: 20,
            },
            user.idToken
          ),
    [queryBase, rangeOver90, productMetric, user.idToken],
    { enabled: detailReady }
  );

  const categories = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardCategories(
            {
              from: range.from,
              to: range.to,
              channel,
              metric: categoryMetric,
              period: range.period,
              limit: 5,
            },
            user.idToken
          ),
    [queryBase, rangeOver90, categoryMetric, user.idToken],
    { enabled: detailReady }
  );

  const gst = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardGst(
            { from: range.from, to: range.to, channel, period: range.period },
            user.idToken
          ),
    [queryBase, rangeOver90, user.idToken],
    { enabled: detailReady }
  );

  const returns = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardReturns(
            { from: range.from, to: range.to, channel, period: range.period },
            user.idToken
          ),
    [queryBase, rangeOver90, user.idToken],
    { enabled: detailReady }
  );

  const returnsRefunds = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardReturnsTimeseries(
            {
              from: range.from,
              to: range.to,
              channel,
              granularity,
              period: range.period,
            },
            user.idToken
          ),
    [queryBase, rangeOver90, granularity, user.idToken],
    { enabled: detailReady }
  );

  const scheduling = useAsyncResource(
    () =>
      rangeOver90
        ? Promise.resolve(null)
        : getDashboardFulfillmentTimeseries(
            {
              from: range.from,
              to: range.to,
              channel,
              granularity,
              period: range.period,
            },
            user.idToken
          ),
    [queryBase, rangeOver90, granularity, user.idToken],
    { enabled: detailReady }
  );

  const kpis = summary.data?.kpis;
  const delta = compare ? summary.data?.delta || {} : null;
  // Prefer server-resolved IST bounds from any live payload (summary is canonical).
  const apiRange = summary.data?.range;
  const chipFrom = apiRange?.from || range.from;
  const chipTo = apiRange?.to || range.to;
  const profitBasis = summary.data?.profit_basis;
  const productRows = topProducts.data?.items || [];
  const maxProductMetric = Math.max(
    1,
    ...productRows.map((p) => {
      if (productMetric === 'qty') return p.qty || 0;
      if (productMetric === 'profit') return p.gross_profit || 0;
      return p.revenue || 0;
    })
  );
  const categoryRows = categories.data?.items || [];
  const returnsApplicable = returnsRefunds.data?.applicable !== false;
  const fulfillmentApplicable = scheduling.data?.applicable !== false;
  const returnsChartEmpty =
    !returnsApplicable || !(returnsRefunds.data?.buckets || []).length;

  const metricToggle = (value, onChange, options) => (
    <div className={styles.miniTabs}>
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          className={`${styles.miniTab} ${value === opt.key ? styles.miniTabActive : ''}`}
          onClick={() => onChange(opt.key)}
          disabled={opt.disabled}
          title={opt.title}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const granularityToggle = metricToggle(granularity, setGranularity, [
    {
      key: 'hour',
      label: 'Hour',
      disabled: !hourOk,
      title: hourOk ? undefined : 'Hour only for ranges of 2 days or less',
    },
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
  ]);

  const chartXLabel =
    granularity === 'hour' ? 'Hour (IST)' : granularity === 'week' ? 'Week' : 'Date';
  const trendYLabel =
    trendMetric === 'orders' ? 'Orders' : trendMetric === 'gross_profit' ? 'Gross profit (₹)' : 'Revenue (₹)';

  const productMid = Math.ceil(productRows.length / 2);
  const productLeft = productRows.slice(0, productMid);
  const productRight = productRows.slice(productMid);

  const renderProductsTable = (rows) => (
    <div className={styles.tableWrap}>
      <table className={`${styles.table} ${styles.productsTable}`}>
        <thead>
          <tr>
            <th>#</th>
            <th>Product</th>
            <th>Qty</th>
            <th>Net revenue</th>
            {isAdmin && <th>Gross profit</th>}
            {isAdmin && <th>Profit percentage</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const active =
              productMetric === 'qty'
                ? p.qty
                : productMetric === 'profit'
                  ? p.gross_profit
                  : p.revenue;
            return (
              <tr key={p.sku || p.item_id || p.rank}>
                <td className={styles.mono}>{p.rank}</td>
                <td>
                  <div className={styles.productCell}>
                    <strong>{p.name}</strong>
                    <span className={styles.mutedText}>{p.sku}</span>
                    <div className={styles.inlineBarTrack}>
                      <div
                        className={styles.inlineBar}
                        style={{ width: `${(active / maxProductMetric) * 100}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td>{formatCount(p.qty)}</td>
                <td>{formatInr(p.revenue)}</td>
                {isAdmin && <td>{formatInr(p.gross_profit)}</td>}
                {isAdmin && <td>{formatPct(p.margin_pct)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Insights"
        title="Analytics"
        subtitle="Sales, profit, revenue, reports"
        actions={
          <button
            type="button"
            className={styles.refreshBtn}
            onClick={() => setRefreshKey((k) => k + 1)}
            title="Refresh"
          >
            <RefreshCw size={15} />
            <span>{formatAsOf(summary.data?.as_of)}</span>
          </button>
        }
      />

      <div className={styles.controls}>
        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Time range</span>
          <div className={styles.segments}>
            {PERIOD_PRESETS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`${styles.segment} ${preset === key ? styles.segmentActive : ''}`}
                onClick={() => setPreset(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {preset === 'custom' && (
          <div className={styles.dateFilters}>
            <label className={styles.dateField}>
              <span>From</span>
              <input
                type="date"
                className={styles.dateInput}
                value={customFrom || range.from}
                max={customTo || range.to}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </label>
            <label className={styles.dateField}>
              <span>To</span>
              <input
                type="date"
                className={styles.dateInput}
                value={customTo || range.to}
                min={customFrom || range.from}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </label>
          </div>
        )}

        <div className={styles.rangeChip} title="Resolved range (IST)">
          {formatRangeLabel(chipFrom, chipTo)}
          <span className={styles.istTag}>IST</span>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Channel</span>
          <div className={styles.segments}>
            {CHANNELS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`${styles.segment} ${channel === key ? styles.segmentActive : ''}`}
                onClick={() => setChannel(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Compare</span>
          <label className={styles.compareCheck}>
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
            />
            vs previous period
          </label>
        </div>

        <ReportsToolbar
          reports={reports}
          from={range.from}
          to={range.to}
          channel={channel}
          rangeTooLong={reportRangeTooLong}
          dayCount={dayCount}
        />
      </div>

      {compare && compareWindow && (
        <p className={styles.compareHint}>
          Comparing to {formatShortRange(compareWindow.from, compareWindow.to)}
        </p>
      )}

      <ReportsHistory reports={reports} />

      {rangeOver90 ? (
        <div className={styles.over90}>
          <AlertTriangle size={18} />
          <div>
            <strong>Range is over 90 days — live widgets off</strong>
            <p>
              Inclusive days: {dayCount} (max {MAX_DASHBOARD_DAYS}). Narrow the range for the
              dashboard, or generate a report above (up to {MAX_REPORT_DAYS} days).
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className={`${styles.kpiStrip} ${isAdmin ? '' : styles.kpiStripOps}`}>
            <StatCard
              icon={IndianRupee}
              label="Revenue"
              value={summary.loading ? '…' : formatInr(kpis?.revenue)}
              delta={delta?.revenue_pct}
              showDelta={compare}
              tint="#F2B922"
            />
            {isAdmin && (
              <StatCard
                icon={TrendingUp}
                label="Gross profit"
                value={summary.loading ? '…' : formatInr(kpis?.gross_profit)}
                sublabel={
                  [
                    kpis?.profit_margin_pct != null
                      ? `${formatPct(kpis.profit_margin_pct)} margin`
                      : null,
                    profitBasis === 'line_cost_snapshot' ? 'Line cost snapshot' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
                delta={delta?.gross_profit_pct}
                showDelta={compare}
                tint="#2E7D32"
              />
            )}
            <StatCard
              icon={ShoppingBag}
              label="Orders"
              value={summary.loading ? '…' : formatCount(kpis?.orders)}
              sublabel={
                kpis?.items_sold != null ? `${formatCount(kpis.items_sold)} items` : undefined
              }
              delta={delta?.orders_pct}
              showDelta={compare}
              tint="#2F6FED"
            />
            <StatCard
              icon={Package}
              label="Avg order value"
              value={
                summary.loading
                  ? '…'
                  : kpis?.aov == null
                    ? '—'
                    : formatInr(kpis.aov, { compact: false })
              }
              delta={delta?.aov_pct}
              showDelta={compare}
              tint="#0D9488"
            />
          </div>
          {summary.error && (
            <div className={styles.inlineBanner}>
              {summary.error}
              <Button variant="outline" size="sm" onClick={summary.reload}>
                Retry
              </Button>
            </div>
          )}

          <WidgetShell
            title="Net revenue"
            loading={summary.loading}
            error={summary.error}
            empty={!kpis}
            onRetry={summary.reload}
          >
            <WaterfallChart kpis={kpis} />
          </WidgetShell>
        </>
      )}

      <div className={styles.mainCol}>
          {!rangeOver90 && (
            <>
              <div className={styles.trendChannelRow}>
                <WidgetShell
                  title="Trend"
                  loading={timeseries.loading}
                  error={timeseries.error}
                  empty={!(timeseries.data?.buckets || []).length}
                  onRetry={timeseries.reload}
                  actions={
                    <>
                      {granularityToggle}
                      {metricToggle(trendMetric, setTrendMetric, [
                        { key: 'revenue', label: 'Revenue' },
                        { key: 'orders', label: 'Orders' },
                        ...(isAdmin ? [{ key: 'gross_profit', label: 'Gross profit' }] : []),
                      ])}
                    </>
                  }
                >
                  <TrendChart
                    buckets={timeseries.data?.buckets || []}
                    channel={channel}
                    metric={trendMetric}
                    compare={compare}
                    xAxisLabel={chartXLabel}
                    yAxisLabel={trendYLabel}
                  />
                </WidgetShell>

                <WidgetShell
                  title="Revenue by channel"
                  className={styles.channelBesideTrend}
                  loading={channels.loading}
                  error={channels.error}
                  empty={!(channels.data?.channels || []).length}
                  onRetry={channels.reload}
                >
                  <ChannelDonut
                    channels={channels.data?.channels || []}
                    totalRevenue={channels.data?.total_revenue}
                  />
                </WidgetShell>
              </div>

              <div className={styles.twoUp}>
                {returnsApplicable ? (
                  <WidgetShell
                    title="Returns & exchange"
                    loading={returnsRefunds.loading}
                    error={returnsRefunds.error}
                    empty={returnsChartEmpty}
                    emptyTitle="No returns in this range"
                    onRetry={returnsRefunds.reload}
                  >
                    <DualLineChart
                      buckets={returnsRefunds.data?.buckets || []}
                      series={RETURNS_EXCHANGE_SERIES}
                      valueType="count"
                      ariaLabel="Returns and exchange over time"
                      xAxisLabel={chartXLabel}
                      yAxisLabel="Count"
                    />
                  </WidgetShell>
                ) : (
                  <WidgetShell
                    title="Returns & exchange"
                    empty
                    emptyTitle="Not applicable for POS"
                  />
                )}

                {fulfillmentApplicable ? (
                  <WidgetShell
                    title="Scheduling"
                    loading={scheduling.loading}
                    error={scheduling.error}
                    empty={!(scheduling.data?.buckets || []).length}
                    onRetry={scheduling.reload}
                  >
                    <DualLineChart
                      buckets={scheduling.data?.buckets || []}
                      series={SCHEDULING_SERIES}
                      valueType="count"
                      ariaLabel="Scheduled, completed, and delivery failed over time"
                      xAxisLabel={chartXLabel}
                      yAxisLabel="Orders"
                    />
                  </WidgetShell>
                ) : (
                  <WidgetShell
                    title="Scheduling"
                    empty
                    emptyTitle="Not applicable for POS"
                  />
                )}
              </div>

              <WidgetShell
                title="Top products"
                loading={topProducts.loading}
                error={topProducts.error}
                empty={!productRows.length}
                onRetry={topProducts.reload}
                actions={metricToggle(productMetric, setProductMetric, [
                  { key: 'revenue', label: 'Net revenue' },
                  { key: 'qty', label: 'Qty' },
                  ...(isAdmin ? [{ key: 'profit', label: 'Gross profit' }] : []),
                ])}
              >
                <div className={styles.productsSplit}>
                  {renderProductsTable(productLeft)}
                  {productRight.length > 0 && renderProductsTable(productRight)}
                </div>
              </WidgetShell>

              <div className={styles.twoUp}>
                <WidgetShell
                  title="Payments"
                  loading={payments.loading}
                  error={payments.error}
                  empty={!(payments.data?.methods || payments.data?.payments || []).length}
                  onRetry={payments.reload}
                >
                  <PaymentsBars
                    payments={payments.data?.methods || payments.data?.payments || []}
                    collectedTotal={payments.data?.collected_total}
                  />
                </WidgetShell>

                <WidgetShell
                  title="GST breakup"
                  loading={gst.loading}
                  error={gst.error}
                  empty={!(gst.data?.rows || []).length}
                  onRetry={gst.reload}
                >
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Rate</th>
                          <th>Taxable</th>
                          <th>Tax</th>
                          <th>CGST</th>
                          <th>SGST</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const rows = gst.data?.rows || [];
                          const maxTax = Math.max(1, ...rows.map((entry) => entry.tax || 0));
                          return rows.map((row) => (
                            <tr key={row.rate}>
                              <td>{row.rate}%</td>
                              <td>{formatInr(row.taxable)}</td>
                              <td>
                                <div className={styles.gstTaxCell}>
                                  {formatInr(row.tax)}
                                  <div className={styles.inlineBarTrack}>
                                    <div
                                      className={styles.inlineBar}
                                      style={{ width: `${((row.tax || 0) / maxTax) * 100}%` }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td>{formatInr(row.cgst)}</td>
                              <td>{formatInr(row.sgst)}</td>
                            </tr>
                          ));
                        })()}
                        {(gst.data?.totals) && (
                          <tr className={styles.totalRow}>
                            <td>Total</td>
                            <td>{formatInr(gst.data.totals.taxable)}</td>
                            <td>{formatInr(gst.data.totals.tax)}</td>
                            <td>{formatInr(gst.data.totals.cgst)}</td>
                            <td>{formatInr(gst.data.totals.sgst)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className={styles.captionLine}>Prices are GST-inclusive. Not a GSTR filing.</p>
                </WidgetShell>
              </div>

              <div className={styles.twoUp}>
                <WidgetShell
                  title="Top categories"
                  loading={categories.loading}
                  error={categories.error}
                  empty={!categoryRows.length}
                  onRetry={categories.reload}
                  actions={metricToggle(categoryMetric, setCategoryMetric, [
                    { key: 'revenue', label: 'Revenue' },
                    { key: 'qty', label: 'Qty' },
                    ...(isAdmin ? [{ key: 'profit', label: 'Profit' }] : []),
                  ])}
                >
                  <CategoriesBars
                    categories={categoryRows.slice(0, 5)}
                    metric={categoryMetric}
                    isAdmin={isAdmin}
                  />
                </WidgetShell>

                <WidgetShell
                  title="Returns in range"
                  loading={returns.loading}
                  error={returns.error}
                  empty={!returns.data}
                  onRetry={returns.reload}
                >
                  <div className={styles.returnTiles}>
                    <div className={styles.returnTile}>
                      <Banknote size={16} />
                      <div>
                        <strong>{formatInr(returns.data?.credits_issued)}</strong>
                        <span>
                          Credits issued · {formatCount(returns.data?.credit_count)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.returnTile}>
                      <strong>{formatCount(returns.data?.exchange_count)}</strong>
                      <span>Exchanges</span>
                    </div>
                    <div className={styles.returnTile}>
                      <strong>{formatCount(returns.data?.failed_pickup_count)}</strong>
                      <span>Failed pickups</span>
                    </div>
                  </div>
                  <Link to="/returns" className={styles.viewAllLink}>
                    View all in Returns →
                  </Link>
                </WidgetShell>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
